import { useQuery } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { GET_PAGE_QUERY } from '@/api/getPage'
import PageBlocks from '@/components/page/PageBlocks'
import FreeShippingBanner from '@/components/shipping/FreeShippingBanner'
import Container from '@/components/Container'
import { useSeo } from '@/hooks/useSeo'
import { useThemeStore } from '@/store/theme'

interface HomePageProps {
  onNavigateToCategory: (slug: string) => void
}

export default function HomePage({ onNavigateToCategory: _onNavigateToCategory }: HomePageProps) {
  const { t } = useTranslation()
  const storeName = useThemeStore((store) => store.tenantTheme.name)

  const { data, loading, error } = useQuery(GET_PAGE_QUERY, {
    variables: {
      slug: 'home-page',
    },
  })

  const page = data?.getPage

  // Above the early returns — hooks can't be conditional, and the loading and
  // error states still need a title rather than whatever the last route left.
  useSeo({
    // The home title is the store name alone, so no page title is passed: a
    // "Home | Store" title wastes the most valuable part of the SERP snippet.
    description: page?.seo?.description,
    canonicalPath: '/',
  })

  if (loading) {
    return (
      <div className="w-full flex justify-center py-3 md:py-8">
        {t('common.homeLoading')}
      </div>
    )
  }

  if (error || !page) {
    return (
      <div className="w-full flex justify-center py-3 md:py-8 text-danger">
        {error ? error.message : t('common.homeLoadError')}
      </div>
    )
  }

  return (
    <div className="w-full flex flex-col pb-12">
      {/* The home page is entirely CMS-driven and shows no visible title, but a
          document still needs exactly one h1 for the heading order to be valid. */}
      <h1 className="sr-only">{page.title || storeName}</h1>
      <section className="mx-auto w-full">
        <PageBlocks blocks={page.blocks} />
      </section>
      <Container as="section" className="w-full mt-8">
        <FreeShippingBanner variant="list" />
      </Container>
    </div>
  )
}
